import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Plus, ExternalLink, UserPlus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useProjects } from '@/hooks/useProjects';
import { useCustomers, useCreateCustomer } from '@/hooks/useCustomers';
import { toast } from 'sonner';

interface CreateProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-compila il cliente (per "Nuovo progetto" da customer detail). */
  initialCustomerId?: string;
}

const FREE_TEXT_VALUE = '__free_text__';

const CreateProjectDialog = ({ open, onOpenChange, initialCustomerId }: CreateProjectDialogProps) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    customer_id: '' as string,
    client_free_text: '' as string,
  });

  // Quando il dialog si apre con initialCustomerId, pre-popola il cliente
  useEffect(() => {
    if (open && initialCustomerId) {
      setFormData(f => ({ ...f, customer_id: initialCustomerId }));
    }
  }, [open, initialCustomerId]);

  const { createProject, isCreating } = useProjects();
  const { data: customers = [] } = useCustomers();
  const createCustomer = useCreateCustomer();
  const [inlineCustomerOpen, setInlineCustomerOpen] = useState(false);
  const [quickCustomer, setQuickCustomer] = useState({ name: '', email: '', type: 'company' as 'company' | 'individual' });

  const selectedCustomer = useMemo(
    () => customers.find(c => c.id === formData.customer_id) ?? null,
    [customers, formData.customer_id],
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    // Cliente: o da anagrafica (customer_id) o da testo libero (client legacy).
    // Almeno uno dei due deve essere valorizzato per non rompere il campo "client"
    // che e` NOT NULL nel DB legacy.
    const clientText = selectedCustomer
      ? selectedCustomer.name
      : formData.client_free_text.trim();
    if (!clientText) return;

    createProject(
      {
        name: formData.name.trim(),
        client: clientText,
        description: formData.description.trim(),
        customer_id: selectedCustomer?.id ?? null,
      },
      {
        onSuccess: () => {
          setFormData({ name: '', description: '', customer_id: '', client_free_text: '' });
          onOpenChange(false);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Nuovo Progetto</DialogTitle>
          <DialogDescription>
            Crea un nuovo progetto. Collegalo a un cliente dell'anagrafica per
            l'intestazione automatica dei preventivi.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nome Progetto *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(f => ({ ...f, name: e.target.value }))}
                placeholder="es. Ristrutturazione Uffici Milano"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="customer">Cliente *</Label>
              <div className="flex gap-2">
                <Select
                  value={formData.customer_id || FREE_TEXT_VALUE}
                  onValueChange={(v) => setFormData(f => ({
                    ...f,
                    customer_id: v === FREE_TEXT_VALUE ? '' : v,
                  }))}
                >
                  <SelectTrigger id="customer" className="flex-1">
                    <SelectValue placeholder="Seleziona dall'anagrafica..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={FREE_TEXT_VALUE}>
                      <span className="italic text-muted-foreground">— Testo libero —</span>
                    </SelectItem>
                    {customers.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        <div className="flex items-center gap-2">
                          <span>{c.name}</span>
                          {c.city && (
                            <span className="text-xs text-muted-foreground">
                              ({c.city}{c.province ? `, ${c.province}` : ''})
                            </span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    setQuickCustomer({ name: formData.client_free_text, email: '', type: 'company' });
                    setInlineCustomerOpen(true);
                  }}
                  title="Crea nuovo cliente"
                >
                  <UserPlus className="h-4 w-4" />
                </Button>
              </div>
              {!formData.customer_id && (
                <Input
                  value={formData.client_free_text}
                  onChange={(e) => setFormData(f => ({ ...f, client_free_text: e.target.value }))}
                  placeholder="Scrivi nome cliente (non in anagrafica)"
                  required={!formData.customer_id}
                />
              )}
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Link to="/customers" className="underline hover:text-foreground inline-flex items-center gap-1">
                  Gestisci anagrafica clienti <ExternalLink className="h-3 w-3" />
                </Link>
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descrizione</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(f => ({ ...f, description: e.target.value }))}
                placeholder="Descrizione del progetto..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isCreating}>
              Annulla
            </Button>
            <Button type="submit" disabled={isCreating}>
              <Plus className="h-4 w-4 mr-2" />
              {isCreating ? 'Creazione...' : 'Crea Progetto'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      {/* Mini-dialog "Crea cliente rapido" senza navigation */}
      <Dialog open={inlineCustomerOpen} onOpenChange={setInlineCustomerOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Crea nuovo cliente</DialogTitle>
            <DialogDescription>
              Solo dati essenziali — puoi completare l'anagrafica dopo da Clienti.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="qc-name">Nome / Ragione sociale *</Label>
              <Input
                id="qc-name"
                value={quickCustomer.name}
                onChange={(e) => setQuickCustomer(c => ({ ...c, name: e.target.value }))}
                placeholder="es. Mario Rossi SRL"
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="qc-email">Email</Label>
              <Input
                id="qc-email"
                type="email"
                value={quickCustomer.email}
                onChange={(e) => setQuickCustomer(c => ({ ...c, email: e.target.value }))}
                placeholder="opzionale"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <Select
                value={quickCustomer.type}
                onValueChange={(v) => setQuickCustomer(c => ({ ...c, type: v as 'company' | 'individual' }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="company">Azienda</SelectItem>
                  <SelectItem value="individual">Privato</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInlineCustomerOpen(false)} disabled={createCustomer.isPending}>
              Annulla
            </Button>
            <Button
              disabled={createCustomer.isPending || !quickCustomer.name.trim()}
              onClick={async () => {
                try {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  const created: any = await createCustomer.mutateAsync({
                    name: quickCustomer.name.trim(),
                    type: quickCustomer.type,
                    email: quickCustomer.email.trim() || undefined,
                  });
                  if (created?.id) {
                    setFormData(f => ({ ...f, customer_id: created.id, client_free_text: '' }));
                  }
                  toast.success('Cliente creato');
                  setInlineCustomerOpen(false);
                } catch (e) {
                  console.error('[inline customer create] error:', e);
                  toast.error('Errore creazione cliente');
                }
              }}
            >
              {createCustomer.isPending ? 'Creazione…' : 'Crea cliente'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default CreateProjectDialog;
